import React, { useState } from "react";
import { Card, Modal } from "reactstrap";

import ActivationCodeService from "services/activationCode";
import { MAX_ACTIVATION_CODE_TIMES, MIN_ACTIVATION_CODE_TIMES } from "constants/lengthValidity";

const COULD_NOT_SAVE = "The change could not be saved. Please try again.";

/** How a code's switch reads in the list and here. A code from a server without the switch is active. */
export const statusLabel = isActive => (isActive === false ? "Off" : "Active");

const limitLabel = limit => (limit === null ? "Unlimited" : String(limit));

/** The code's limit as stored: the list leaves numberOfUses out when there is none (unlimited). */
export const currentLimitOf = (code) => {
    const value = code && code.numberOfUses;
    return value === undefined || value === null || value === "" || Number(value) === 0 ? null : Number(value);
};

/**
 * What the limit is measured against: the server's usesSoFar (every use for a public code, the most
 * uses by one person for a code sent to a list of email addresses); usedCount from an older server.
 */
export const usesSoFarOf = code => (typeof code.usesSoFar === "number" ? code.usesSoFar : Number(code.usedCount) || 0);

/**
 * The PATCH body for what changed: only the fields that differ from the code as loaded, plus the
 * code's updatedAt as the page loaded it, so a change someone else made since is refused (409), not
 * overwritten. Empty when nothing changed.
 *
 * @param {object} code  the row from the list
 * @param {{limit: (number|null), isActive: boolean}} chosen
 * @returns {object}
 */
export function limitsRequest(code, { limit, isActive }) {
    const body = {};

    if (limit !== currentLimitOf(code)) {
        body.numberOfUses = limit;
    }
    if (isActive !== (code.isActive !== false)) {
        body.isActive = isActive;
    }
    if (Object.keys(body).length && code.updatedAt) {
        body.expectedUpdatedAt = code.updatedAt;
    }

    return body;
}

/**
 * "Edit limit / on-off" for one activation code - super admins and Admins (the server enforces it).
 * Changes only the limit and the on/off switch, through PATCH /:id/limits; the dates, the code, the
 * service and the email list stay as they are (the full Edit form is for those).
 *
 * Two steps: the form, then a confirmation showing the uses so far, the limit before -> after and the
 * switch before -> after. The server's answer - e.g. that the limit is now below the uses, or that
 * someone else changed the code meanwhile - is shown here, in the modal.
 */
export default function EditLimitsModal({ code, onClose, onSaved }) {
    const allowListed = Boolean(code.hasAttachedEmails);
    const uses = usesSoFarOf(code);
    const storedLimit = currentLimitOf(code);
    const wasActive = code.isActive !== false;

    // An allow-listed code's limit is per person and cannot be unlimited (the server refuses it).
    const [unlimited, setUnlimited] = useState(storedLimit === null && !allowListed);
    const [limitText, setLimitText] = useState(storedLimit === null ? "" : String(storedLimit));
    const [isActive, setIsActive] = useState(wasActive);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const typed = limitText.trim();
    // An allow-listed code saved with no limit (the full Edit form allows it) keeps that until a limit
    // is typed, so it can still be switched on or off on its own (the server accepts that).
    const keepNoLimit = allowListed && storedLimit === null && typed === "";
    const limit = unlimited || keepNoLimit ? null : Number(typed);

    let limitProblem = "";
    if (!unlimited && !keepNoLimit) {
        if (!/^\d+$/.test(typed) || limit < MIN_ACTIVATION_CODE_TIMES || limit > MAX_ACTIVATION_CODE_TIMES) {
            limitProblem = `Enter a whole number from ${MIN_ACTIVATION_CODE_TIMES} to ${MAX_ACTIVATION_CODE_TIMES}.`;
        } else if (limit !== storedLimit && limit < uses) {
            limitProblem = `The limit can't be lower than the uses so far (${uses}).`;
        }
    }

    const body = limitProblem ? {} : limitsRequest(code, { limit, isActive });
    const nothingChanged = !limitProblem && !Object.keys(body).length;

    const usesText = allowListed
        ? `${uses} (the most by one person - this code's limit is per person)`
        : String(uses);
    const limitChange = limit === storedLimit
        ? `${limitLabel(storedLimit)} (unchanged)`
        : `${limitLabel(storedLimit)} → ${limitLabel(limit)}`;
    const statusChange = isActive === wasActive
        ? `${statusLabel(wasActive)} (unchanged)`
        : `${statusLabel(wasActive)} → ${statusLabel(isActive)}`;

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const response = await ActivationCodeService.updateActivationCodeLimits(code.id, body);
            onSaved(response && response.data);
        } catch (failure) {
            setError((failure && failure.message) || COULD_NOT_SAVE);
            setSaving(false);
        }
    };

    return (
        <Modal isOpen className="modal-size" centered>
            <Card className="p-4 mb-0" data-testid="limits-modal">
                <header className="font-size-22">{`Edit limit / on-off - ${code.code}`}</header>

                {!confirming ? (
                    <div className="mt-4">
                        <p className="mb-3" data-testid="limits-uses">{`Uses so far: ${usesText}`}</p>

                        <label className="d-block mb-1" htmlFor="limits-limit">
                            {allowListed ? "Limit per person" : "Limit (uses by everyone together)"}
                        </label>
                        <input
                            id="limits-limit"
                            data-testid="limits-limit"
                            className="form-control"
                            type="number"
                            min={MIN_ACTIVATION_CODE_TIMES}
                            max={MAX_ACTIVATION_CODE_TIMES}
                            value={unlimited ? "" : limitText}
                            disabled={unlimited}
                            onChange={(event) => setLimitText(event.target.value)}
                        />
                        {!allowListed && (
                            <label className="d-flex align-items-center mt-2 mb-0">
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    data-testid="limits-unlimited"
                                    checked={unlimited}
                                    onChange={(event) => setUnlimited(event.target.checked)}
                                />
                                Unlimited
                            </label>
                        )}
                        {limitProblem ? <p className="text-danger mt-2 mb-0" data-testid="limits-problem">{limitProblem}</p> : null}

                        <fieldset className="mt-4">
                            <legend className="font-size-14 mb-1">Status</legend>
                            <label className="mr-4">
                                <input
                                    type="radio"
                                    className="mr-2"
                                    name="limits-status"
                                    data-testid="limits-on"
                                    checked={isActive}
                                    onChange={() => setIsActive(true)}
                                />
                                Active
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    className="mr-2"
                                    name="limits-status"
                                    data-testid="limits-off"
                                    checked={!isActive}
                                    onChange={() => setIsActive(false)}
                                />
                                Off
                            </label>
                        </fieldset>

                        {nothingChanged ? <p className="text-muted mt-2 mb-0">Nothing has changed yet.</p> : null}

                        <section className="d-flex flex-row justify-content-between">
                            <button
                                className="btn btn-primary w-100 mt-4 mr-3"
                                data-testid="limits-continue"
                                disabled={Boolean(limitProblem) || nothingChanged}
                                onClick={() => setConfirming(true)}
                            >
                                Continue
                            </button>
                            <button className="btn btn-outline-primary w-100 mt-4 ml-3" onClick={onClose}>Cancel</button>
                        </section>
                    </div>
                ) : (
                    <div className="mt-4" data-testid="limits-confirm-step">
                        <ul className="list-unstyled mb-0" data-testid="limits-summary">
                            <li>{`Uses so far: ${usesText}`}</li>
                            <li>{`Limit: ${limitChange}`}</li>
                            <li>{`Status: ${statusChange}`}</li>
                        </ul>
                        {!isActive && wasActive ? (
                            <p className="mt-3 mb-0">
                                While it is off, patients cannot check or pay with this code. Bookings already made with it are not affected.
                            </p>
                        ) : null}
                        {limit !== null && limit !== storedLimit && limit === uses ? (
                            <p className="mt-3 mb-0">
                                {allowListed
                                    ? "Nobody who has already used it that many times can use it again."
                                    : "The limit equals the uses so far: nobody else can use it."}
                            </p>
                        ) : null}
                        {error ? <p className="text-danger mt-3 mb-0" role="alert" data-testid="limits-error">{error}</p> : null}

                        <section className="d-flex flex-row justify-content-between">
                            <button
                                className="btn btn-primary w-100 mt-4 mr-3"
                                data-testid="limits-confirm"
                                disabled={saving}
                                onClick={save}
                            >
                                Confirm
                            </button>
                            <button
                                className="btn btn-outline-primary w-100 mt-4 ml-3"
                                disabled={saving}
                                onClick={() => { setError(""); setConfirming(false); }}
                            >
                                Back
                            </button>
                        </section>
                    </div>
                )}
            </Card>
        </Modal>
    );
}
